import React, { createContext, useContext, useEffect, useState } from 'react'
import { getSession, onAuthStateChange, signOut } from '@/lib/session'
import { resetSupabase } from '@/lib/supabase'

interface AuthContextType {
  session: any | null
  user: any | null
  loading: boolean
  signOut: () => Promise<any>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<any | null>(null)
  const [user, setUser] = useState<any | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let unsub: (() => void) | undefined

    const init = async () => {
      setLoading(true)
      try {
        const s = await getSession()
        setSession(s)
        setUser(s?.user ?? null)
      } catch (err) {
        console.error('[AuthProvider] getSession error', err)
        setSession(null)
        setUser(null)
      } finally {
        setLoading(false)
      }
      unsub = onAuthStateChange((_ev, s2) => {
        setSession(s2)
        setUser(s2?.user ?? null)
      })
    }

    init()

    // Revalidate session when browser regains connectivity
    const handleOnline = async () => {
      console.log('[AuthProvider] online event - revalidating session')
      try {
        // Recreate Supabase client to ensure realtime/ws reconnects
        resetSupabase()

        const s = await getSession()
        setSession(s)
        setUser(s?.user ?? null)
      } catch (err) {
        console.error('[AuthProvider] error revalidating session on online', err)
      }
    }
    window.addEventListener('online', handleOnline)

    return () => {
      window.removeEventListener('online', handleOnline)
      unsub?.()
    }
  }, [])

  return (
    <AuthContext.Provider value={{ session, user, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (ctx === undefined) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
